"use client";

// Ported from the private `packages/sdk/react/src/tools/define-tool-view.tsx`
// (plan 030). Two deliberate deltas, recorded in
// plans/030a-tool-view-strategy.md §2.5:
//   1. Invocation re-keyed from the server-side `@workspace/ai` `ChatToolUIPart`
//      to the block-local structural `ToolViewInvocation` (./invocation) —
//      extracted views never import server packages.
//   2. `AutoJsonBody` renders the block's `ToolIoBlock` JSON blocks instead of
//      `ai-elements`' `ToolInput`/`ToolOutput`, which are not published
//      registry items. No extracted family uses the auto-body fallback
//      (every extracted view supplies `body`), so this path is cosmetic-only
//      and never on the pilots' render path.

import type { ComponentType, ReactNode } from "react";
import { safeStringify, ToolIoBlock } from "../../tool-part";
import type { ToolViewInvocation } from "./invocation";
import { ToolItem } from "./tool-item";

/**
 * The one place the standard tool-row state machine lives.
 *
 * Nearly every Magnus tool view renders the same shape: a shimmering label
 * while the tool runs, then a compact `ToolItem` row that optionally expands
 * to show results. Historically each view re-implemented the same
 * `output-error -> loading -> soft-error -> empty -> done` ladder by hand.
 * `defineToolView` captures that ladder once; a view becomes a small config
 * object describing only what actually varies (icon, per-state labels, an
 * optional expandable body). Tools with genuinely bespoke UI (image cards,
 * maps, multi-panel results) still author their own component.
 */

/** Normalized view of a tool invocation passed to every config callback. */
export type ToolViewSnapshot<TInput = any, TOutput = any, TLive = undefined> = {
  /** The raw part type, e.g. `tool-bash` or `tool-notion_search`. */
  type: string;
  input: TInput | undefined;
  output: TOutput | undefined;
  state: ToolViewInvocation["state"];
  /** Hard transport/tool error — only set when `state === "output-error"`. */
  errorText: string | undefined;
  /**
   * Value returned by `useLiveState`, or `undefined` when the view didn't
   * configure one. Client tools (e.g. Office) use this to surface live
   * executor status and an elapsed-time counter alongside the part.
   */
  live: TLive;
};

/**
 * Shared `softError` reader for the ubiquitous discriminated-union envelope
 * `{ ok: true, ... } | { ok: false, error }`. Returns the error message only
 * when the tool reported `ok === false`, so `defineToolView` renders an error
 * row for a soft (non-transport) failure. Capabilities whose failure shape
 * differs (e.g. Slack's bare top-level `error`) supply their own reader.
 */
export function okEnvelopeSoftError(snap: {
  output: { ok?: boolean; error?: string } | undefined;
}): string | undefined {
  const out = snap.output;
  return out && out.ok === false ? out.error : undefined;
}

type Label<S> = string | ((snap: S) => string);

function resolveLabel<S>(label: Label<S> | undefined, snap: S): string {
  if (label === undefined) {
    return "";
  }
  return typeof label === "function" ? label(snap) : label;
}

export type DefineToolViewConfig<
  TInput = any,
  TOutput = any,
  TLive = undefined,
> = {
  /** Leading icon, rendered in the fixed-width gutter (use `toolIconClass`). */
  icon: ReactNode;
  /** Optional icon swapped in on the error row (e.g. a destructive alert). */
  errorIcon?: ReactNode;
  /** Label shown (shimmering) while the tool is running. */
  loading: Label<ToolViewSnapshot<TInput, TOutput, TLive>>;
  /** Label shown once the tool has completed successfully. */
  done: Label<ToolViewSnapshot<TInput, TOutput, TLive>>;
  /**
   * Label shown when the tool errored (hard error or `softError`). Defaults to
   * the `done` label when omitted; the error message itself renders separately.
   */
  error?: Label<ToolViewSnapshot<TInput, TOutput, TLive>>;
  /** Muted mono detail shown next to the done label (e.g. a query or id). */
  detail?: (
    snap: ToolViewSnapshot<TInput, TOutput, TLive>
  ) => string | undefined;
  /**
   * Muted mono detail shown next to the shimmering loading label (e.g. a live
   * elapsed-time counter for client tools). Omit for the common case where the
   * loading row is label-only.
   */
  loadingDetail?: (
    snap: ToolViewSnapshot<TInput, TOutput, TLive>
  ) => string | undefined;
  /** Muted trailing info shown next to the done label (e.g. "Timed out"). */
  info?: (snap: ToolViewSnapshot<TInput, TOutput, TLive>) => string | undefined;
  /**
   * Detect a failure encoded in an otherwise-successful output (e.g.
   * `{ ok: false, error }`). Return the message to render an error row.
   */
  softError?: (
    snap: ToolViewSnapshot<TInput, TOutput, TLive>
  ) => string | undefined;
  /**
   * Detect an empty-but-successful result so it renders a plain, non-expandable
   * row (e.g. "No results"). Evaluated only in the done branch.
   */
  empty?: {
    when: (snap: ToolViewSnapshot<TInput, TOutput, TLive>) => boolean;
    label: Label<ToolViewSnapshot<TInput, TOutput, TLive>>;
  };
  /**
   * Override loading detection. Defaults to "no output yet and not errored",
   * which matches the common case. Streaming tools that emit partial output
   * with an in-progress flag (search, bash) provide their own predicate.
   */
  isLoading?: (snap: ToolViewSnapshot<TInput, TOutput, TLive>) => boolean;
  /** Custom expandable body. Omit to fall back to auto JSON (see `autoBody`). */
  body?: (snap: ToolViewSnapshot<TInput, TOutput, TLive>) => ReactNode;
  /**
   * When `body` is omitted, render the raw input/output as collapsible JSON.
   * Defaults to `true`; set `false` for tools whose row should never expand.
   */
  autoBody?: boolean;
  /**
   * Render `body` always-visible beneath the row instead of behind an
   * expand chevron. For small, glanceable content (e.g. a single link).
   */
  inlineBody?: boolean;
  /**
   * Optional hook run on every render to derive live state from the raw
   * invocation (e.g. a client-tool executor status + elapsed timer). Its
   * return value is exposed to the other callbacks as `snap.live`. Because
   * the config is fixed per view, this is called consistently and is
   * safe to use React hooks inside.
   */
  useLiveState?: (invocation: ToolViewInvocation) => TLive;
};

function readInvocation(
  invocation: ToolViewInvocation
): Omit<ToolViewSnapshot, "live"> {
  const state = invocation.state;
  return {
    type: invocation.type,
    input: invocation.input,
    output: "output" in invocation ? invocation.output : undefined,
    state,
    // An `output-error` part is a failure even when the server sent no
    // message: without this fallback a blank `errorText` renders the whole
    // row as a success, which is the worst direction to get wrong.
    errorText:
      invocation.state === "output-error"
        ? invocation.errorText?.trim() || "Tool call failed"
        : undefined,
  };
}

type JsonBodySnapshot = {
  input: unknown;
  output: unknown;
  errorText: string | undefined;
};

function AutoJsonBody({ snap }: { snap: JsonBodySnapshot }) {
  const hasInput = snap.input !== undefined;
  const hasResult = snap.output !== undefined || snap.errorText !== undefined;
  return (
    <>
      {hasInput && (
        <ToolIoBlock label="Parameters">
          {safeStringify(snap.input)}
        </ToolIoBlock>
      )}
      {hasResult && (
        <ToolIoBlock label="Result" tone={snap.errorText ? "error" : "default"}>
          {snap.errorText ?? safeStringify(snap.output)}
        </ToolIoBlock>
      )}
    </>
  );
}

/**
 * Build a standard tool view component from a declarative config. The returned
 * component matches the `ToolView` contract (`{ invocation }`) and can be
 * registered directly or wrapped in `lazy`.
 */
export function defineToolView<TInput = any, TOutput = any, TLive = undefined>(
  config: DefineToolViewConfig<TInput, TOutput, TLive>
): ComponentType<{ invocation: ToolViewInvocation }> {
  const {
    icon,
    autoBody = true,
    isLoading = (snap) => snap.output == null && snap.state !== "output-error",
  } = config;

  /**
   * Pure renderer for the ladder — no hooks of its own, so it can be called
   * from either component variant below. `ToolItem`'s own hooks run when React
   * renders the returned element, not when this function is invoked.
   */
  function renderView(
    snap: ToolViewSnapshot<TInput, TOutput, TLive>
  ): ReactNode {
    if (isLoading(snap)) {
      return (
        <ToolItem
          detail={config.loadingDetail?.(snap)}
          icon={icon}
          label={resolveLabel(config.loading, snap)}
          loading
        />
      );
    }

    const errorMessage = snap.errorText ?? config.softError?.(snap);

    const bodyNode = config.body
      ? config.body(snap)
      : autoBody
        ? renderAutoBody(snap)
        : null;
    const hasBody = bodyNode != null && bodyNode !== false;
    const expandable = hasBody && !config.inlineBody;

    if (errorMessage) {
      return (
        <ToolItem
          detail={config.detail?.(snap)}
          error={errorMessage}
          expandable={expandable}
          icon={config.errorIcon ?? icon}
          label={resolveLabel(config.error ?? config.done, snap)}
        >
          {bodyNode}
        </ToolItem>
      );
    }

    if (config.empty?.when(snap)) {
      return (
        <ToolItem icon={icon} label={resolveLabel(config.empty.label, snap)} />
      );
    }

    return (
      <ToolItem
        detail={config.detail?.(snap)}
        expandable={expandable}
        icon={icon}
        info={config.info?.(snap)}
        label={resolveLabel(config.done, snap)}
      >
        {bodyNode}
      </ToolItem>
    );
  }

  // Pick the component variant once at definition time (not per render) so that
  // `useLiveState` — which may itself call React hooks — is always invoked
  // unconditionally within whichever variant we return.
  const { useLiveState } = config;
  if (useLiveState) {
    return function StandardToolViewWithLiveState({
      invocation,
    }: {
      invocation: ToolViewInvocation;
    }) {
      const live = useLiveState(invocation);
      return renderView({
        ...readInvocation(invocation),
        live,
      } as ToolViewSnapshot<TInput, TOutput, TLive>);
    };
  }

  return function StandardToolView({
    invocation,
  }: {
    invocation: ToolViewInvocation;
  }) {
    return renderView({
      ...readInvocation(invocation),
      live: undefined,
    } as ToolViewSnapshot<TInput, TOutput, TLive>);
  };
}

/** JSON fallback body, rendered only when there's something to show. */
function renderAutoBody(snap: JsonBodySnapshot): ReactNode {
  const hasInput = snap.input !== undefined;
  const hasOutput = snap.output !== undefined;
  if (!(hasInput || hasOutput || snap.errorText)) {
    return null;
  }
  return <AutoJsonBody snap={snap} />;
}
